import React, { useRef, useState, useContext, useEffect } from "react";
// import MediaQuery from 'react-responsive';
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  FormControl,
  InputGroup,
  Col,
  Button,
  Row,
  Container,
} from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { reducer } from "../../../context/";
import actionType from "../../../context/actionType";
import AssetListItem from "../../assets/screens/AssetListScreen/components/AssetList/Sections/ListItems/AssetListItem";
import GeneralProjectItem from "../../assets/screens/AssetListScreen/components/AssetList/Sections/ListItems/GeneralProjectItem";
import { Warning } from "../FormScreenStyles";
import FormDropdown from "../FormDropdown";
import ErrorBox from "../../../components/ErrorBox/ErrorBox";
import Api from "../../../helpers/Api";
import googleKeys from "../../../configs/googleKeys";
import { SmallText } from "../../../components/Text";
import { logResponse } from "../../../helpers/Logger";
import { isInvalidSession, resetSession } from "../../../helpers/ErrorHandler";

// TributeTypeEnum
const PREFIX_OPTIONS = [
  "Supported by",
  "In honour of",
  "In memory of",
  "Anonymous",
];

const ASSET_ICON_PATH = {
  tree: "/donation/assets/icons/treePlaque.png",
  bench: "/donation/assets/icons/benchPlaque.png",
  block: "/donation/assets/icons/blockPlaque.png",
} as { [key: string]: string };

let captchaRef: any;

interface Props extends RouteComponentProps {}

const NamePlate: React.FunctionComponent<Props> = (props) => {
  const { t, i18n } = useTranslation();
  const [recaptchaLg, setRecaptchaLg] = useState(
    i18n.language === "en" ? "en" : i18n.language === "zhTW" ? "zh-TW" : "zh-CN"
  );
  const [loadEnd, setLoadEnd] = useState(false);
  useEffect(() => {
    setRecaptchaLg(
      i18n.language === "en"
        ? "en"
        : i18n.language === "zhTW"
        ? "zh-TW"
        : "zh-CN"
    );

    if (captchaRef && captchaRef.reset) {
      captchaRef.reset();
    }
  }, [i18n.language]);

  let isChinese = i18n.language === "zhTW" || i18n.language === "cn";

  const authOnLoad = useRef(sessionStorage.getItem("auth")); // track if initial load is null, if yes, wait until token is true to call API
  const { formReducer, configReducer } = useContext(reducer);
  const isGeneralProject = !!formReducer.state.selectedProject;
  const titleOptions = isGeneralProject
    ? PREFIX_OPTIONS.slice(1)
    : PREFIX_OPTIONS;
  const [verified, setVerified] = useState(false);
  const [tributeName, setTributeName] = useState("");
  const [tributeAmount, setTributeAmount] = useState(0);
  const [tributeAmountEmpty, setTributeAmountEmpty] = useState(false);
  const [tributeType, setTributeType] = useState(PREFIX_OPTIONS[1]);

  const [gProjectID, setGProjectID] = useState("");
  const [error, setError] = useState({
    blockedName: false,
    blockedAsset: false,
    generalProjectExpired: false,
  });
  const toggleLoadingOverlay = (showLoading: boolean) => {
    configReducer.dispatch({
      type: actionType.config.TOGGLE_LOADING,
      payload: {
        showLoading,
      },
    });
  };
  const recaptchaValidation = (token: string) => {
    const auth = sessionStorage.getItem("auth");
    const headers = {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: "" + auth,
    };
    fetch(
      `${Api.path(
        window.location
      )}/common/recaptcha?g-recaptcha-response=${token}&purpose=nameplate`,
      {
        method: "GET",
        headers: headers,
      }
    )
      .then((result) => {
        if (result.status === 401) {
          throw "Invalid Session";
        }
        return result.json();
      })
      .then((resJson) => {
        logResponse(
          configReducer.state.debugMode,
          `common/recaptcha: ${resJson.msg}`
        );
        if (isInvalidSession(resJson)) {
          throw "Invalid Session";
        }
        if (resJson.msg == "OK") {
          setVerified(true);
        } else {
          if (captchaRef && captchaRef.reset) {
            captchaRef.reset();
          }
        }
      })
      .catch(() => {
        resetSession(formReducer);
      });
  };
  const callNameplateApi = (name: string, tributeType: string) => {
    const auth = sessionStorage.getItem("auth");
    const headers = {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: "" + auth,
    };
    // remove ellipsis from prefix or set to null for Anonymous due to TributeTypeEnum check
    let sanitiseTributeType =
      tributeType === "Anonymous" ? null : tributeType.replace("...", "");
    if (name.length == 0) {
      sanitiseTributeType = null;
    }
    fetch(`${Api.path(window.location)}/donate/nameplate`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        tributeName: name,
        tributeType: sanitiseTributeType,
      }),
    })
      .then((result) => {
        if (result.status === 401) {
          throw "Invalid Session";
        }
        return result.json();
      })
      .then((resJson) => {
        logResponse(
          configReducer.state.debugMode,
          `donate/nameplate: ${resJson.msg}`
        );
        if (isInvalidSession(resJson)) {
          throw "Invalid Session";
        }
        if (resJson.msg == "OK") {
          setVerified(true);

          // TODO: check if local check for empty tributeName necessary
          if (props.history && props.history.push) {
            props.history.push("/auth/user/landing");
            formReducer.dispatch({
              type: actionType.form.GO_TO_FORM_STAGE,
              payload: {
                nextStep: 2,
              },
            });
          }
        }
        if (resJson.msg.toLowerCase().startsWith("blocked")) {
          // name plaque name is blocked, please choose another
          setError({
            blockedAsset: false,
            blockedName: true,
            generalProjectExpired: false,
          });
        }
        // response.result returned false, failed, display error message
        // toast.error(resJson.msg || 'Unknown error sending request, please try again.');
        if (
          resJson.msg == "Fail to lock the asset, please choose another one."
        ) {
          // asset has been selected / blocked
          setError({
            blockedAsset: true,
            blockedName: false,
            generalProjectExpired: false,
          });
        }
        if (captchaRef && captchaRef.reset) {
          captchaRef.reset();
        }
      })
      .catch(() => {
        resetSession(formReducer);
      });
  };

  function loadTributeFromSession(nameplateStorage: any) {
    const prefill = JSON.parse(nameplateStorage);
    const parsedType = prefill.tributeType
      ? `${prefill.tributeType}${
          prefill.tributeType === "Anonymous" ? "" : "..."
        }`
      : PREFIX_OPTIONS[1];
    setTributeType(parsedType);
    setTributeName(prefill.tributeName || "");
  }

  useEffect(() => {
    if (tributeType !== "Anonymous" && !tributeName) {
      const nameplateStorage = sessionStorage.getItem("SESSION_NAMEPLATE");
      if (nameplateStorage) loadTributeFromSession(nameplateStorage);
    }
    if (tributeType === "Anonymous") {
      setTributeName("");
    }
  }, [tributeType]);

  useEffect(() => {
    const nameplateStorage = sessionStorage.getItem("SESSION_NAMEPLATE");
    if (nameplateStorage) loadTributeFromSession(nameplateStorage);
  }, []);

  const handleNameplateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // setVerified(false); // change of text, need to re-verify
    // if(captchaRef && captchaRef.reset) {
    //     captchaRef.reset();
    // }
    const NOT_WS_EN_ZH_REGEX_MATCH =
      /[^\s\w\u2E80-\u2FD5\u3190-\u319f\u3400-\u4DBF\u4E00-\u9FCC\uF900-\uFAAD]/gi;
    setTributeName(
      event.target.value.replace
        ? event.target.value.replace(NOT_WS_EN_ZH_REGEX_MATCH, "")
        : event.target.value
    );
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTributeAmountEmpty(false);
    setTributeAmount(
      Number(
        event.target.value.replace
          ? event.target.value.replace(/\D/g, "")
          : event.target.value
      )
    );
  };

  useEffect(() => {
    // TODO optimise checking and loading, refactor re-used code
    // TODO use case where auth is expired
    if (document.location && document.location.search) {
      const auth = sessionStorage.getItem("auth");
      if (!configReducer.state.hasToken) {
        return; // initial session does not have token do not call Asset or Project without auth token for payment idenitifcation
      }
      const headers = {
        "Content-Type": "application/json; charset=utf-8",
        Authorization: "" + auth,
      };
      var searchParams = new URLSearchParams(document.location.search);
      if (searchParams.has("generalProject")) {
        if (searchParams.has("amount")) {
          const amount = searchParams.get("amount"); // only set amount for general project
          setTributeAmount(Number(amount) || 0);
        }
        const projectId = searchParams.get("generalProject");
        setGProjectID(projectId);

        fetch(`${Api.path(window.origin)}generalProject/${projectId}`, {
          method: "GET",
          headers,
        })
          .then(function (response) {
            if (response.status === 401) {
              throw "Invalid Session";
            }
            return response.json();
          })
          .then((resJson) => {
            logResponse(
              configReducer.state.debugMode,
              `generalProject/\{id\}: ${resJson.msg}`
            );
            if (isInvalidSession(resJson)) {
              throw "Invalid Session";
            }
            if (resJson.msg == "OK") {
              console.info("Project Json - ");
              //console.info(resJson);
              //console.info(resJson.data.donationForm);

              if (resJson.data.status !== "OnShelf") {
                setError({
                  blockedAsset: false,
                  blockedName: false,
                  generalProjectExpired: true,
                });
                setTimeout(() => {
                  props.history.push("/");
                }, 5000);
              }
              // skip captcha when fundCode end with _SKIP
              if (resJson.data.fundCode.match(/^(.*)_SKIP$/)) {
                console.log("skiping capcha");
                setVerified(true);
                recaptchaValidation(resJson.data.fundCode);
              }
              console.log(resJson.data.status);
              sessionStorage.setItem(
                "SELECTED_PROJECT",
                JSON.stringify(resJson.data)
              );
              sessionStorage.removeItem("SELECTED_ASSET");
              formReducer.dispatch({
                type: actionType.form.START_FORM_STAGE,
                payload: {
                  selectedProject: resJson.data,
                },
              });
              formReducer.dispatch({
                type: actionType.form.SET_SCREEN,
                payload: {
                  screen: "nameplate",
                },
              });
              setLoadEnd(true);
            }
          })
          .catch(() => {
            resetSession(formReducer);
          });
        return;
      }
      // TODO need an asset to check if asset is locked/exists or not
      if (searchParams.has("asset")) {
        const assetId = searchParams.get("asset");
        fetch(`${Api.path(window.origin)}/asset/${assetId}`, {
          method: "GET",
          headers,
        })
          .then(function (response) {
            if (response.status === 401) {
              throw "Invalid Session";
            }
            return response.json();
          })
          .then((resJson) => {
            logResponse(
              configReducer.state.debugMode,
              `asset/\{id\}: ${resJson.msg}`
            );
            if (isInvalidSession(resJson)) {
              throw "Invalid Session";
            }
            if (resJson.msg == "OK") {
              sessionStorage.setItem(
                "SELECTED_ASSET",
                JSON.stringify(resJson.data)
              );
              sessionStorage.removeItem("SELECTED_PROJECT");
              formReducer.dispatch({
                type: actionType.form.START_FORM_STAGE,
                payload: { selectedAsset: resJson.data },
              });
              formReducer.dispatch({
                type: actionType.form.SET_SCREEN,
                payload: {
                  screen: "nameplate",
                },
              });
              setLoadEnd(true);
            }
          })
          .catch(() => {
            resetSession(formReducer);
          });
        return;
      }
    }
    const asset = sessionStorage.getItem("SELECTED_ASSET");
    const project = sessionStorage.getItem("SELECTED_PROJECT");
    const selected = asset
      ? { selectedAsset: JSON.parse(asset) }
      : project
      ? { selectedProject: JSON.parse(project) }
      : undefined;
    if (selected) {
      formReducer.dispatch({
        type: actionType.form.START_FORM_STAGE,
        payload: selected,
      });
    }
    formReducer.dispatch({
      type: actionType.form.SET_SCREEN,
      payload: {
        screen: "nameplate",
      },
    });
  }, [configReducer.state.hasToken]);

  useEffect(() => {
    if (error.blockedAsset) {
      setTimeout(() => {
        window.location.href = "/";
      }, 5000);
    }
  }, [error.blockedAsset]);

  // Confirm Name Plaque Button
  const renderButton = (style = {}) => (
    <Button
      size={"lg"}
      onClick={() => {
        if (isGeneralProject && !tributeAmount) {
          setTributeAmountEmpty(true);
          return;
        }
        sessionStorage.setItem(
          "DONATION_AMOUNT",
          JSON.stringify(tributeAmount)
        );
        callNameplateApi(tributeName, tributeType);
      }}
      variant={"primary"}
      disabled={!verified}
      style={{
        ...style,
        ...(isChinese
          ? { fontFamily: "Microsoft JhengHei" }
          : { fontFamily: "Rambla" }),
        width: "301px",
        height: "57px",
        backgroundColor: "#2c3e50",
        color: verified ? "#FFFFFF" : "#78848f",
        borderColor: "#2c3e50",
      }}
    >
      {t("next")}
    </Button>
  );

  const assetType =
    formReducer.state.selectedAsset && formReducer.state.selectedAsset.type;
  let assetPath = ASSET_ICON_PATH.block; // default or unknown asset type
  let paddingAmt = 20;
  let imageMaxWidth = 420;
  let imageMinWidth = 400;
  let color = "#000000";
  var searchParams = new URLSearchParams(document.location.search);
  if (assetType === "Tree") {
    color = "#ffffff";
    assetPath = ASSET_ICON_PATH.tree;
    imageMaxWidth = 320;
    imageMinWidth = 280;
  }
  if (assetType === "Bench") {
    assetPath = ASSET_ICON_PATH.bench;
    imageMaxWidth = 500;
    imageMinWidth = 460;
  }
  if (isMobile) {
    imageMaxWidth = imageMaxWidth * 0.8;
    imageMinWidth = imageMinWidth * 0.8;
  }

  console.info("Random Loc ");
  console.info(formReducer.state.selectedProject);

  const renderComponent = () => (
    <Container
      style={{
        width: "100%",
        padding: 0,
        ...(isMobile ? { paddingLeft: 8, paddingRight: 8 } : {}),
      }}
    >
      <Helmet>
        <meta
          property="og:description"
          content="Name plaque description and name"
        />
      </Helmet>
      {loadEnd && (
        <>
          <div
            style={{
              ...(isChinese
                ? { fontFamily: "Microsoft JhengHei" }
                : { fontFamily: "Rambla" }),
              fontSize: "1.4rem",
              display: "flex",
              borderStyle: "solid",
              borderWidth: "0px 0px 1px 0px",
              borderColor: "black",
              paddingTop: "10px",
            }}
          >
            {t("selectedItem")}
          </div>
          <Row style={{ margin: 0, padding: 0 }}>
            <Col style={{ margin: 0, padding: 0 }}>
              {formReducer.state.selectedAsset && (
                <AssetListItem
                  displayImage
                  asset={formReducer.state.selectedAsset}
                  hideButton
                  nameplateDisplay
                  isMobile={isMobile}
                />
              )}
              {formReducer.state.selectedProject && (
                <GeneralProjectItem
                  item={formReducer.state.selectedProject}
                  hideButton
                  nameplateDisplay
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col>
              <Row
                style={{
                  padding: 0,
                  margin: 0,
                  marginBottom: 16,
                  ...(isMobile ? { flexDirection: "column" } : {}),
                }}
              >
                {!searchParams.has("generalProject") && (
                  <div
                    style={{
                      display: "flex",
                      minWidth: "10vw",
                      marginRight: 15,
                      ...(isMobile ? { marginTop: 12 } : {}),
                    }}
                  >
                    <FormDropdown
                      options={titleOptions}
                      menuStyle={{
                        backgroundColor: "#6c757d",
                        minWidth: 150,
                        height: 38,
                        borderRadius: ".25rem",
                        ...(isMobile
                          ? { maxWidth: "45vw" }
                          : { maxWidth: "12vw" }),
                      }}
                      textStyle={{
                        color: "white",
                        ...(isChinese
                          ? { fontFamily: "Microsoft JhengHei" }
                          : { fontFamily: "Rambla" }),
                        fontSize: "100%",
                      }}
                      currentSelection={t(tributeType) || tributeType}
                      placeholder=""
                      language={i18n.language}
                      onClick={(tributeType: any) =>
                        setTributeType(tributeType)
                      }
                    />
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    flex: 1,
                    ...(isMobile
                      ? { marginTop: 12 }
                      : searchParams.has("generalProject")
                      ? {}
                      : { marginLeft: 15 }),
                    ...(isGeneralProject ? { flexDirection: "column" } : {}),
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      ...(gProjectID != null &&
                      gProjectID != "" &&
                      !["2", "3", "4", "5"].includes(gProjectID)
                        ? {}
                        : { display: "none" }),
                    }}
                  >
                    <SmallText>{t("displayName")}</SmallText>

                    <InputGroup>
                      <InputGroup.Prepend>
                        <InputGroup.Text
                          style={{
                            ...(isChinese
                              ? { fontFamily: "Microsoft JhengHei" }
                              : { fontFamily: "Rambla" }),
                          }}
                        >
                          {isGeneralProject
                            ? t("acknowledgementName")
                            : t("nameplaque")}
                        </InputGroup.Text>
                      </InputGroup.Prepend>
                      <FormControl
                        style={{
                          ...(isChinese
                            ? { fontFamily: "Microsoft JhengHei" }
                            : { fontFamily: "Rambla" }),
                        }}
                        disabled={tributeType === "Anonymous"}
                        onChange={(event: any) => handleNameplateChange(event)}
                        value={tributeName}
                        placeholder={`${
                          /*    t(

                          tributeType === "Anonymous"
                            ? "thankYouSupport"
                            : "displayName"
                        )*/
                          t(
                            tributeType === "Anonymous"
                              ? "thankYouSupport"
                              : "leaveBlank"
                          )
                        }`}
                        aria-label="email"
                      />
                    </InputGroup>
                    {/*                     <SmallText>{t("leaveBlank")}</SmallText>
                     */}
                  </div>
                  {isGeneralProject ? (
                    <Row style={{ padding: 0, margin: 0, marginTop: 16 }}>
                      <div
                        style={{
                          display: "flex",
                          flex: 1,
                          flexDirection: "column",
                        }}
                      >
                        <InputGroup>
                          <InputGroup.Prepend>
                            <InputGroup.Text
                              style={{
                                ...(isChinese
                                  ? { fontFamily: "Microsoft JhengHei" }
                                  : { fontFamily: "Rambla" }),
                              }}
                            >
                              {t("amountHKD")}
                            </InputGroup.Text>
                          </InputGroup.Prepend>
                          <FormControl
                            style={{
                              ...(isChinese
                                ? { fontFamily: "Microsoft JhengHei" }
                                : { fontFamily: "Rambla" }),
                              ...(tributeAmountEmpty
                                ? { borderColor: "red" }
                                : {}),
                            }}
                            onChange={(event: any) => handleAmountChange(event)}
                            value={
                              tributeAmount ? tributeAmount.toString() : ""
                            }
                            placeholder={`${t("donationAmount")}`}
                            maxLength={15}
                          />
                        </InputGroup>
                        {tributeAmountEmpty && (
                          <Warning style={{ marginTop: 8 }}>
                            {t("invalidDonationAmount")}
                          </Warning>
                        )}
                        {/* <SmallText>{t('dummyReference')}</SmallText> */}
                      </div>
                    </Row>
                  ) : null}
                </div>
              </Row>
            </Col>
          </Row>
          {!isGeneralProject && (
            <Row>
              <Col>
                <SmallText>{t("nameplaqueFootnote1")}</SmallText>
                <SmallText>{t("nameplaqueFootnote2")}</SmallText>
                <SmallText>
                  <b>{t("nameplaqueFootnote3")}</b> {t("nameplaqueFootnote4")}
                </SmallText>

                {/*
                t("Included inappropriate language, please change")

                {hasError.nameplate && <Warning>t("errorNameplate")</Warning>}
                
                {hasError.emptyNameplate && <Warning>Nameplate cannot be empty, please enter a display name.</Warning>} */}
              </Col>
            </Row>
          )}
          {!!error.blockedName && (
            <ErrorBox style={{ marginTop: 16 }}>
              <SmallText style={{ color: "#6b6b6b" }}>
                {t("errorNameplate")}
              </SmallText>
              {/* <SmallText style={{ color: '#6b6b6b' }}>The Tribute Name (and Tribute Message for some of the opportunities) will be displayed on the donor plaque in West Kowloon Cultural District. The tribute name can be in form of a person, couple, family, company or organization. To keep all the plaques in West Kowloon consistent, please consider using one of the following honoree formats:</SmallText>
            <SmallText style={{ color: '#6b6b6b' }}>In the name of a person: Peter Chan </SmallText>
            <SmallText style={{ color: '#6b6b6b' }}>In the name of a couple: Mr & Mrs Peter Chan </SmallText>
            <SmallText style={{ color: '#6b6b6b' }}>In the name of a family: Wong's Family </SmallText>
            <SmallText style={{ color: '#6b6b6b' }}>In the name of a company: ANC Limited </SmallText>
            <SmallText style={{ color: '#6b6b6b' }}>In the name of an organisation: Lam Foundation Ltd.</SmallText> */}
            </ErrorBox>
          )}
          {!!error.blockedAsset && (
            <ErrorBox style={{ marginTop: 16 }}>
              <SmallText style={{ color: "#6b6b6b" }}>
                {t("errorFailLockAsset")}
              </SmallText>
            </ErrorBox>
          )}
          {!!error.generalProjectExpired && (
            <ErrorBox style={{ marginTop: 16 }}>
              <SmallText style={{ color: "#6b6b6b" }}>
                {t("generalProjectExpired")}
              </SmallText>
            </ErrorBox>
          )}
          {/* {hasError.message && <Warning>Included inappropriate language, pleasemedia change</Warning>} */}
          {/* {errorMsg && <Warning>{errorMsg}</Warning>} */}
          {!isGeneralProject ? (
            <div
              style={{
                minHeight: isMobile ? 320 : 460,
                minWidth: isMobile ? 320 : 540,
                backgroundImage: `url(${assetPath})`,
                backgroundSize: "contain",
                overflowWrap: "break-word",
                backgroundRepeat: "no-repeat",
                textAlign: "center",
                color,
                backgroundPosition: "center",
                marginBottom: 24,
                ...(isMobile
                  ? {}
                  : {
                      marginTop: 32,
                    }),
              }}
            >
              {isMobile ? (
                <h5
                  style={{
                    ...(isChinese
                      ? { fontFamily: "Microsoft JhengHei" }
                      : { fontFamily: "Rambla" }),
                    paddingTop: 80,
                  }}
                >
                  {tributeType === "Anonymous" ? "" : t(tributeType)}
                </h5>
              ) : (
                <h3
                  style={{
                    ...(isChinese
                      ? { fontFamily: "Microsoft JhengHei" }
                      : { fontFamily: "Rambla" }),
                    marginBottom: 30,
                    paddingTop: 80,
                  }}
                >
                  {tributeType === "Anonymous" ? "" : t(tributeType)}
                </h3>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  paddingLeft: paddingAmt,
                  paddingRight: paddingAmt,
                }}
              >
                {isMobile ? (
                  <div
                    style={{ minWidth: imageMinWidth, maxWidth: imageMaxWidth }}
                  >
                    <SmallText
                      style={{
                        color,
                        ...(isChinese
                          ? { fontFamily: "Microsoft JhengHei" }
                          : { fontFamily: "Rambla" }),
                        fontSize: 16,
                        wordBreak: "break-all",
                        wordWrap: "break-word",
                      }}
                    >
                      {tributeType === "Anonymous"
                        ? t("Anonymous")
                        : tributeName && tributeName.length > 80
                        ? tributeName.slice(0, 79)
                        : tributeName}
                    </SmallText>
                  </div>
                ) : (
                  <div
                    style={{ minWidth: imageMinWidth, maxWidth: imageMaxWidth }}
                  >
                    <h1
                      style={{
                        fontSize: 32,
                        ...(isChinese
                          ? { fontFamily: "Microsoft JhengHei" }
                          : { fontFamily: "Rambla" }),
                        wordBreak: "break-all",
                        wordWrap: "break-word",
                      }}
                    >
                      {tributeType === "Anonymous"
                        ? t("Anonymous")
                        : tributeName && tributeName.length > 80
                        ? tributeName.slice(0, 79)
                        : tributeName}
                    </h1>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{
                display: "none",
                minHeight: isMobile ? 160 : 230,
                minWidth: isMobile ? 160 : 270,
                backgroundImage: `url(/donation/assets/icons/Name-Plaque-GD.jpg)`,
                backgroundSize: "contain",
                overflowWrap: "break-word",
                backgroundRepeat: "no-repeat",
                textAlign: "center",
                color,
                backgroundPosition: "center",
                marginBottom: 24,
                ...(isMobile
                  ? {}
                  : {
                      marginTop: 32,
                    }),
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  paddingLeft: paddingAmt,
                  paddingRight: paddingAmt,
                }}
              >
                {isMobile ? (
                  <div style={{ minWidth: 200, maxWidth: 200, marginTop: 75 }}>
                    <SmallText
                      style={{
                        color: "#fff",
                        ...(isChinese
                          ? { fontFamily: "Microsoft JhengHei" }
                          : { fontFamily: "Rambla" }),
                        fontSize: 16,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {tributeType === "Anonymous"
                        ? t("Anonymous")
                        : tributeName && tributeName.length > 80
                        ? tributeName.slice(0, 79)
                        : tributeName}
                    </SmallText>
                  </div>
                ) : (
                  <div style={{ minWidth: 200, maxWidth: 200, marginTop: 105 }}>
                    <h1
                      style={{
                        color: "#fff",
                        fontSize: 32,
                        ...(isChinese
                          ? { fontFamily: "Microsoft JhengHei" }
                          : { fontFamily: "Rambla" }),
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {tributeType === "Anonymous"
                        ? t("Anonymous")
                        : tributeName && tributeName.length > 80
                        ? tributeName.slice(0, 79)
                        : tributeName}
                    </h1>
                  </div>
                )}
              </div>
            </div>
          )}
          {
            !isGeneralProject ? (
              <SmallText>{t("footnoteTimeout")}</SmallText>
            ) : null
            // <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', marginBottom: 10}}>
            //     <SmallText>
            //         {t('acknowledgementNameWillPlace')}
            //     </SmallText>
            // </div>
          }
          <Row
            style={
              isMobile
                ? { justifyContent: "center" }
                : {
                    alignItems: "center",
                    justifyContent: "flex-end",
                    padding: 0,
                    margin: 0,
                    marginTop: 16,
                  }
            }
          >
            <div style={{ float: "right" }}>
              <ReCAPTCHA
                hl={recaptchaLg}
                ref={(ref: any) => (captchaRef = ref)}
                sitekey={googleKeys.PRO_ReCAPTCHA_KEY}
                onChange={(newToken: string | null) => {
                  recaptchaValidation(newToken || "");
                }}
              />
            </div>
          </Row>
          <Row
            style={
              isMobile
                ? { justifyContent: "center" }
                : {
                    alignItems: "center",
                    justifyContent: "flex-end",
                    padding: 0,
                    margin: 0,
                    marginTop: 16,
                  }
            }
          >
            <div style={{ float: "right" }}>
              {renderButton(isMobile ? { marginTop: 12 } : { marginLeft: 30 })}
            </div>
          </Row>
        </>
      )}
    </Container>
  );

  return (
    <Container
      style={{
        width: "100%",
        padding: 0,
        ...(isMobile ? { paddingLeft: 8, paddingRight: 8 } : {}),
      }}
    >
      <Row style={{ padding: 0, margin: 0 }}>
        <Col style={{ padding: 0, margin: 0 }} xs={0} sm={1} md={1} lg={1} />
        <Col style={{ padding: 0, margin: 0 }} xs={12} sm={10} md={10} lg={10}>
          {renderComponent()}
        </Col>
        <Col style={{ padding: 0, margin: 0 }} xs={0} sm={1} md={1} lg={1} />
      </Row>
    </Container>
  );
};

export default withRouter(NamePlate);
